import React from 'react';
import { Field } from './FormFields';
import SearchableMultiSelect from './SearchableMultiSelect';
import ActionsSelector from './MetaActions';
import PixelEventsSelector from './MetaPixelEvents';
import CustomConversionsSelector from './MetaCustomConversions';
import { META_ADS_METRICS } from './metaAdsConfig';

// META PRIMARY METRICS — what this workspace's Meta account is actually judged
// on, captured in the workspace dialog right after the Meta account is picked.
//
// FOUR dropdowns, ONE stored array. The workspace doc keeps a single
// `primaryMetrics: [{ id, name, kind, actionType?, eventKind? }]` list and each
// dropdown owns the slice with its own `kind`. That is what lets a Meta data
// source tick "Include workspace metrics" and have the server route every entry
// back into the report field that owns it at run time
// (server/services/cognitive-flows/workspaceMetricSelections.js) without a
// fourth storage shape to keep in sync.
//
// The three account-specific dropdowns are the SAME components the Meta data
// source editors use, so what a reader pins here is exactly the column they
// would get by selecting it on a source by hand. Their cost/value toggles are
// deliberately not wired: those are per-report column options, not part of
// "which numbers this client lives by".

const PICKER_STYLES = {
  hint: { fontSize: '12px', color: 'var(--text-secondary, #64748b)', display: 'block' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', fontSize: '13px', background: '#fff',
    color: 'var(--text-primary)',
  },
};

/** Entries of one kind, in the native shape that kind's own picker speaks. */
export function metricsOfKind(list, kind) {
  const rows = (Array.isArray(list) ? list : []).filter((m) => m && m.kind === kind);
  if (kind === 'metric') return rows.map((m) => String(m.id));
  if (kind === 'action') return rows.map((m) => ({ id: String(m.id), name: m.name, actionType: m.actionType || String(m.id) }));
  if (kind === 'pixel_event') {
    // `eventKind` is the pixel event's own standard/custom flag, stored under a
    // different name because `kind` is taken by the routing discriminator.
    return rows.map((m) => ({
      id: String(m.id), name: m.name, kind: m.eventKind || 'custom',
      ...(m.actionType ? { actionType: m.actionType } : {}),
    }));
  }
  return rows.map((m) => ({ id: String(m.id), name: m.name }));
}

/**
 * Replace one kind's slice, keeping every other kind untouched and in order.
 * `next` arrives in that picker's native shape; this is the only place the two
 * vocabularies meet.
 */
export function withMetricsOfKind(list, kind, next) {
  const others = (Array.isArray(list) ? list : []).filter((m) => m && m.kind !== kind);
  const mapped = (Array.isArray(next) ? next : []).map((entry) => {
    if (kind === 'metric') return { id: String(entry), name: String(entry), kind };
    const row = { id: String(entry.id), name: entry.name || String(entry.id), kind };
    if (kind === 'action') row.actionType = entry.actionType || String(entry.id);
    if (kind === 'pixel_event') {
      if (entry.kind) row.eventKind = entry.kind;
      if (entry.actionType) row.actionType = entry.actionType;
    }
    return row;
  });
  return [...others, ...mapped];
}

/**
 * `value` / `onChange` carry the WHOLE primaryMetrics array — the host stores one
 * list, not four.
 */
export default function WorkspaceMetricFields({ accountId, pixelId, value = [], onChange, disabled = false }) {
  const set = (kind) => (next) => onChange(withMetricsOfKind(value, kind, next));

  return (
    <>
      <Field
        label="Metrics"
        info="Meta's built-in numbers — ROAS, spend, CPC and the rest. These need no account lookup."
      >
        <SearchableMultiSelect
          value={metricsOfKind(value, 'metric')}
          options={META_ADS_METRICS.map((m) => ({ value: m, label: m }))}
          onChange={set('metric')}
          styles={PICKER_STYLES}
          disabled={disabled}
          placeholder="— Select metrics —"
          emptyText="No matching metrics"
          summarize={(n) => `${n} metrics selected`}
        />
      </Field>

      <Field
        label="Actions"
        info="Meta actions this account reported in the last 90 days — link clicks, landing page views, engagement, messaging and app events."
      >
        <ActionsSelector
          accountId={accountId}
          selected={metricsOfKind(value, 'action')}
          onChange={set('action')}
          disabled={disabled}
          styles={PICKER_STYLES}
        />
      </Field>

      <Field
        label="Pixel events"
        info="Standard and custom pixel events from this workspace's Meta pixel."
      >
        <PixelEventsSelector
          accountId={accountId}
          pixelId={pixelId}
          selected={metricsOfKind(value, 'pixel_event')}
          onChange={set('pixel_event')}
          disabled={disabled}
          styles={PICKER_STYLES}
        />
      </Field>

      <Field
        label="Custom conversions"
        info="This account's own custom conversions, by name."
      >
        <CustomConversionsSelector
          accountId={accountId}
          selected={metricsOfKind(value, 'custom_conversion')}
          onChange={set('custom_conversion')}
          disabled={disabled}
          styles={PICKER_STYLES}
        />
      </Field>
    </>
  );
}
